import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { Router } from '@angular/router';

import { environment } from './../../../../environments/environment';


// import { RsaEncryptionService } from '../encryption/rsa-encryption.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = environment.apiUrl;
  private readonly defaultHeaders = new HttpHeaders({
    Accept: 'application/json'
  });

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    // private rsaEncryption: RsaEncryptionService
  ) { }

  /**
   * Format error & throw it back
   * @param error The error object
   * @returns An Observable containing the error
   */
  private handleError(error: any): Observable<never> {
    console.error(`Une erreur s'est produite:`, error);

    // Let the interceptor handle the error
    return throwError(() => error);
  }

  /**
   * Enhanced headers method that includes token signatures for sensitive endpoints
   * @param path The endpoint path
   * @returns HttpHeaders with appropriate headers
   */
  private getEnhancedHeaders(path: string): HttpHeaders {
    let headers = this.getJsonHeaders();
    
    // Check if this is a sensitive endpoint that requires token signature
    if (this.isSensitiveEndpoint(path)) {
      // Get appropriate token signature from session storage
      let signature: string | null = null;
      
      if (path.includes('CompleteRegistration')) {
        signature = sessionStorage.getItem('creat_token_signature');
      } else if (path.includes('ResetPassword')) {
        signature = sessionStorage.getItem('reset_token_signature');
      }
      
      if (signature) {
        headers = headers.set('x-token-signature', signature);
      } else {
        console.warn('⚠️ No token signature found for sensitive endpoint');
      }
    }
    
    return headers;
  }

  /**
   * Check if endpoint requires token signature
   * @param path The endpoint path
   * @returns boolean indicating if endpoint is sensitive
   */
  private isSensitiveEndpoint(path: string): boolean {
    const sensitiveEndpoints = [
      'CompleteRegistration',
      'ResetPassword'
    ];
    
    return sensitiveEndpoints.some(endpoint => path.includes(endpoint));
  }

  /**
   * Get method
   * @param path The endpoint URL
   * @param params The parameters to be sent in url
   * @param retryCount Number of retry attempts (default: 0)
   * @returns An `Observable` of any
   */
  get(path: string, params: HttpParams = new HttpParams(), retryCount = 0): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .get(url, { headers, params })
      .pipe(
        retry(retryCount),
        catchError(error => this.handleError(error))
      );
  }
  
  /**
   * Get method for viewing files that returns a blob response
   * @param path The endpoint URL
   * @param params The parameters to be sent in url
   * @returns An `Observable` of Blob
   */
  viewGet(path: string, params: HttpParams = new HttpParams()): Observable<Blob> {
    const url = this.createUrl(path);
    // Use default headers without Content-Type: application/json
    const headers = this.defaultHeaders;

    return this.httpClient
      .get(url, { 
        headers, 
        params, 
        responseType: 'blob'
      })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Post method with enhanced security for sensitive endpoints
   * @param path The endpoint URL
   * @param body The content to send
   * @returns An `Observable` of any
   */
  post(path: string, body: any = {}): Observable<any> {
    const url = this.createUrl(path);
    let headers = this.getEnhancedHeaders(path);

    // If body is FormData, don't set Content-Type as browser will set it with boundary
    if (body instanceof FormData) {
      // For FormData, start with default headers and add signature if needed
      headers = this.defaultHeaders;
      if (this.isSensitiveEndpoint(path)) {
        let signature: string | null = null;

        if (path.includes('CompleteRegistration')) {
          signature = sessionStorage.getItem('creat_token_signature');
        } else if (path.includes('ResetPassword')) {
          signature = sessionStorage.getItem('reset_token_signature');
        }

        if (signature) {
          headers = headers.set('x-token-signature', signature);
        }
      }
    }

    return this.httpClient
      .post(url, body, { headers })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Post method with RSA + AES encryption for sensitive data
   * Use this for login, passwords, and other sensitive operations
   *
   * @param path The endpoint URL
   * @param body The content to encrypt and send
   * @returns An `Observable` of any
   *
   * @example
   * // Instead of:
   * // this.apiService.post('auth/login', { email, password })
   *
   * // Use:
   * // this.apiService.postEncrypted('auth/login', { email, password })
   */
  postEncrypted(path: string, body: any = {}): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    // Encrypt the data using RSA + AES hybrid encryption
    // return from(this.rsaEncryption.prepareEncryptedRequest(body)).pipe(
    //   switchMap(encryptedPayload => {
    //     return this.httpClient
    //       .post(url, encryptedPayload, { headers })
    //       .pipe(catchError(error => this.handleError(error)));
    //   }),
    //   catchError(error => {
    //     console.error('Encryption failed:', error);
    //     return this.handleError(error);
    //   })
    // );
    
    // Fallback to standard post since encryption is missing
    return this.post(path, body);
  }

  /**
   * Put method
   * @param path The endpoint URL
   * @param body The content to replace with
   * @returns An `Observable` of any
   */
  put(path: string, body: any = {}): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .put(url, body, { headers })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Post method for upload files
   * @param path The endpoint URL
   * @param fileData The `FormData` to upload
   * @returns An `Observable` of any
   */
  upload(path: string, fileData: FormData): Observable<any> {
    const url = this.createUrl(path);
    // Don't set Content-Type for FormData as the browser will set it with the boundary
    let headers = new HttpHeaders({ 'ngsw-bypass': '' });

    // Add signature for sensitive endpoints
    if (this.isSensitiveEndpoint(path)) {
      let signature: string | null = null;

      if (path.includes('CompleteRegistration')) {
        signature = sessionStorage.getItem('creat_token_signature');
      } else if (path.includes('ResetPassword')) {
        signature = sessionStorage.getItem('reset_token_signature');
      }

      if (signature) {
        headers = headers.set('x-token-signature', signature);
      }
    }

    return this.httpClient
      .post(url, fileData, {
        reportProgress: true,
        observe: 'body',
        headers
      })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Post method to handle export
   * @param path The endpoint URL
   * @param body The content to filter with
   * @returns An `Observable` of Blob
   */
  export(path: string, body: any = {}): Observable<Blob> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .post(url, body, { headers, responseType: 'blob' })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Delete method
   * @param path The endpoint URL
   * @param params The parameters to be sent in url
   * @returns An `Observable` of any
   */
  delete(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .delete(url, { headers, params })
      .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates full URL with API base URL
   * @param path Endpoint path
   * @returns Full URL string
   */
  private createUrl(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Gets the file serving base URL (without /api suffix)
   * @returns Base URL for file serving
   */
  getFileServingUrl(): string {
    return this.baseUrl.replace(/\/api$/, '');
  }

  /**
   * Gets headers for JSON requests
   * @returns HttpHeaders with JSON content type
   */
  private getJsonHeaders(): HttpHeaders {
    return this.defaultHeaders.set('Content-Type', 'application/json');
  }

  exportExcel(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .get(url, {
        headers,
        params,
        responseType: 'blob'
      })
      .pipe(catchError(error => this.handleError(error)));
  }

  patch(path: string, body: any = {}): Observable<any> {
    const url = this.createUrl(path);
    const headers = this.getEnhancedHeaders(path);

    return this.httpClient
      .patch(url, body, { headers })
      .pipe(catchError(error => this.handleError(error)));
  }
}